import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { apiClient } from "@/config/api";
import type { RootState } from "@/types/store";
import {
  ChangePasswordBody,
  LoginBody,
  OnboardUserBody,
  RequestVerificationCodeBody,
  SetPasswordBody,
  SubmitPasswordResetBody,
  UploadDocumentsBody,
  VerificationCodeBody,
  VerifySecondaryEmailQuery,
} from "./types";

export const loginUser = createAsyncThunk(
  "auth/login",
  async (body: LoginBody, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      body.token = state.Auth.mfaToken;
      const response = await apiClient.post("/auth/login", body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Login failed"
        );
      }
      return rejectWithValue("Login failed");
    }
  }
);

export const onboardUser = createAsyncThunk(
  "/onboarding/create-account",
  async (body: OnboardUserBody, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("firstName", body.firstName);
      formData.append("lastName", body.lastName);
      formData.append("phoneNumber", body.phoneNumber);
      if (body.secondaryEmail)
        formData.append("secondaryEmail", body.secondaryEmail);
      if (body.profilePicture)
        formData.append("profilePicture", body.profilePicture);

      const response = await apiClient.post(
        "/onboarding/create-account",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Onboarding failed"
        );
      }
      return rejectWithValue("Onboarding failed");
    }
  }
);

export const setPassword = createAsyncThunk(
  "auth/create-password",
  async (
    { body, token }: { body: SetPasswordBody; token: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post(
        `/auth/create-password?token=${token}`,
        body
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Password setup failed"
        );
      }
      return rejectWithValue("Password setup failed");
    }
  }
);

export const verifyUser = createAsyncThunk(
  "auth/confirm-login-mfa",
  async (body: VerificationCodeBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("/auth/confirm-login-mfa", body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "MFA failed"
        );
      }
      return rejectWithValue("MFA failed");
    }
  }
);

export const initiatePasswordReset = createAsyncThunk(
  "auth/initiate-password-reset",
  async (body: RequestVerificationCodeBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        "/auth/initiate-password-reset",
        body
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Password reset failed"
        );
      }
      return rejectWithValue("Password reset failed");
    }
  }
);

export const submitPasswordReset = createAsyncThunk(
  "auth/reset-password",
  async (body: SubmitPasswordResetBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("/auth/reset-password", body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Password reset failed"
        );
      }
      return rejectWithValue("Password reset failed");
    }
  }
);

export const verifyPasswordReset = createAsyncThunk(
  "auth/password-reset-token",
  async (body: VerificationCodeBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("/auth/password-reset-token", body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Password reset verification failed"
        );
      }
      return rejectWithValue("Password reset verification failed");
    }
  }
);

export const verifySecondaryEmail = createAsyncThunk(
  "auth/verify-secondary-email",
  async (query: VerifySecondaryEmailQuery, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/auth/verify-secondary-email?token=${query.token}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Secondary email verification failed"
        );
      }
      return rejectWithValue("Secondary email verification failed");
    }
  }
);

export const resendMfa = createAsyncThunk(
  "auth/resend-login-mfa",
  async (body: RequestVerificationCodeBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("/auth/resend-login-mfa", body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Resend MFA email failed"
        );
      }
      return rejectWithValue("Resend MFA email failed");
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/change-password",
  async (body: ChangePasswordBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("/auth/change-password", body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Password change failed"
        );
      }
      return rejectWithValue("Password change failed");
    }
  }
);

export const goBackOnboarding = createAsyncThunk(
  "/onboarding/back",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("/onboarding/back");
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Go back failed"
        );
      }
      return rejectWithValue("Go back failed");
    }
  }
);

/**
 * @deprecated This was used for homeowner onboarding upload documents step, which has been removed.
 * The ONBOARDING_UPLOAD_DOCUMENTS status no longer exists. This function is kept for backward compatibility
 * but is unreachable in the current homeowner onboarding flow.
 */
export const uploadDocuments = createAsyncThunk(
  "/onboarding/upload-documents",
  async (body: UploadDocumentsBody, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("ach", body.ach);
      formData.append("liability", body.liability);
      if (body.sop) formData.append("sop", body.sop);
      const response = await apiClient.post(
        "/onboarding/upload-documents",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Upload documents failed"
        );
      }
      return rejectWithValue("Upload documents failed");
    }
  }
);

export const getMeInfo = createAsyncThunk(
  "user/me",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("user/me");
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get current user failed"
        );
      }
      return rejectWithValue("Get current user failed");
    }
  }
);
